import type { AssignedAgent } from '../types/contact.types';
import type { FormattedUser } from '../hooks/graphql/users/useOrganizationUsers';

/**
 * Resolve the assigned-agent display data for a contact.
 *
 * Priority:
 *  1. `assignedAgent` snapshot returned by the backend (authoritative when present).
 *  2. Fallback: look up `assignedAgentId` in the org users list and synthesize.
 *
 * Returns `null` when neither source resolves — callers should render "—".
 */
export function resolveAssignedAgent(
  assignedAgent: AssignedAgent | null | undefined,
  assignedAgentId: string | null | undefined,
  users: FormattedUser[]
): AssignedAgent | null {
  if (assignedAgent) return assignedAgent;

  if (assignedAgentId) {
    const u = users.find((user) => user.id === assignedAgentId);
    if (u) {
      return {
        agentId: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email ?? null,
        phone: u.phone_number ?? null,
        photoUrl: u.photoUrl ?? null,
      };
    }
  }

  return null;
}

/** Format an AssignedAgent to a trimmed "First Last" string. Empty when absent. */
export function formatAssignedAgentName(agent: AssignedAgent | null | undefined): string {
  if (!agent) return '';
  return `${agent.firstName ?? ''} ${agent.lastName ?? ''}`.trim();
}
